import React, { useCallback } from "react";

import Button from "veneer/Button";
import Grid from "veneer/Grid";
import { DeleteIconButton } from "veneer/IconButton";
import TextInput from "veneer/TextInput";
import { OverflowTooltipTypography } from "veneer/Typography";

import { isVideo } from "../utils";

interface MediaUploadFile {
  duration: number | string;
  fname: string;
  name: string;
  rawFile: File;
}

export interface MediaUploadFileFormProps {
  disabled?: boolean;
  file: MediaUploadFile;
  onRemove?: (fname: string) => void;
  onUpdate?: (
    file: MediaUploadFile,
    field: "duration" | "name",
    value: string,
  ) => void;
  onUpload?: (file: MediaUploadFile) => void;
}

export default function MediaUploadFileForm({
  disabled = false,
  file,
  onRemove = () => {},
  onUpdate = () => {},
  onUpload = () => {},
}: MediaUploadFileFormProps) {
  const { duration, fname, name, rawFile } = file;
  const isVideoFile = isVideo(rawFile.type);

  const handleRemove = useCallback(() => {
    onRemove(file.fname);
  }, [file.fname, onRemove]);

  const handleUpload = useCallback(() => {
    onUpload(file);
  }, [file, onUpload]);

  const handleUpdate = useCallback(
    (field: "duration" | "name") => (value: string | null) => {
      onUpdate(file, field, value ?? "");
    },
    [file, onUpdate],
  );

  const handleUpdateDuration = useCallback(
    (value: string | null) => {
      handleUpdate("duration")(value);
    },
    [handleUpdate],
  );

  const handleUpdateName = useCallback(
    (value: string | null) => {
      handleUpdate("name")(value);
    },
    [handleUpdate],
  );

  return (
    <Grid
      alignItems="center"
      container
      dataTestId="media-upload-file-form"
      spacing={2}
    >
      <Grid item xs={1}>
        <DeleteIconButton disabled={disabled} onClick={handleRemove} />
      </Grid>

      <Grid item xs={3}>
        <OverflowTooltipTypography variant="body2">
          {fname}
        </OverflowTooltipTypography>
      </Grid>

      <Grid item xs={4}>
        <TextInput
          disabled={disabled}
          fullWidth
          label="Content Name"
          onChange={handleUpdateName}
          value={name}
        />
      </Grid>

      <Grid item xs={2}>
        {isVideoFile ? null : (
          <TextInput
            disabled={disabled}
            endAdornment="sec"
            fullWidth
            label="Duration"
            onChange={handleUpdateDuration}
            value={typeof duration === "number" ? String(duration) : duration}
          />
        )}
      </Grid>

      <Grid item xs={2}>
        <Button
          disabled={disabled}
          icon="add"
          onClick={handleUpload}
          variant="outlined"
        >
          Add
        </Button>
      </Grid>
    </Grid>
  );
}
